import { useCallback, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useNotificationChannels } from "@/features/channels/hooks/useNotificationChannels";
import { getActiveChannels } from "@/features/channels/channelManifest";
import { SKILL_TAXONOMY } from "@/lib/skills";
import {
  buildChecklist,
  missingEnforced,
  PHONE_PROVIDERS,
  type Checklist,
  type ChecklistFacts,
  type StepId,
} from "./checklist";

const LANGUAGE_SET = new Set(SKILL_TAXONOMY.Languages ?? []);
import { readFlags, setFlag, subscribeFlags, type ChecklistFlag } from "./checklistStorage";

export type GettingStarted =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      checklist: Checklist;
      missingEnforced: StepId[];
      facts: ChecklistFacts;
      completedSeen: boolean;
    };

// Stable reference for useSyncExternalStore while signed out.
const NO_FLAGS = { emailOnlyAlerts: false, visitedMyWork: false, completedSeen: false };

/**
 * Membership + "have I ever written" in two `limit 1` probes. RLS lets a
 * member read their own workspace rows, so this needs no RPC. Applying alone
 * creates no membership — the creator accepting does — which is what locks
 * the chat step.
 */
async function fetchChatFacts(userId: string) {
  const [member, sent] = await Promise.all([
    supabase.from("workspace_members").select("quest_id").eq("user_id", userId).limit(1),
    supabase.from("workspace_messages").select("id").eq("sender_id", userId).limit(1),
  ]);
  if (member.error) throw new Error(member.error.message);
  if (sent.error) throw new Error(sent.error.message);
  return { isMember: (member.data ?? []).length > 0, hasSent: (sent.data ?? []).length > 0 };
}

/**
 * When `enabled` is false (or there is no signed-in user) this returns
 * `{ status: "loading" }` permanently, so callers must decide whether the gate
 * applies BEFORE reading `status` and must never block on a disabled hook.
 */
export function useGettingStarted(options?: {
  enabled?: boolean;
  channelPollMs?: number;
}): GettingStarted {
  const { userId } = useAuth();
  const enabled = (options?.enabled ?? true) && !!userId;

  const profile = useProfile(undefined, { enabled });
  const channels = useNotificationChannels({ pollMs: options?.channelPollMs, enabled });
  const chat = useQuery({
    queryKey: ["getting-started-chat", userId],
    queryFn: () => fetchChatFacts(userId!),
    enabled,
  });
  const flags = useSyncExternalStore(subscribeFlags, () =>
    userId ? readFlags(userId) : NO_FLAGS,
  );

  if (!enabled) return { status: "loading" };
  // A source is errored only if it has nothing to show: a failed background
  // refetch must not flip an already-loaded checklist to error.
  if (
    (profile.isError && !profile.data) ||
    (channels.isError && !channels.data) ||
    (chat.isError && !chat.data)
  )
    return { status: "error" };
  if (!profile.data || !channels.data || !chat.data) return { status: "loading" };

  const active = getActiveChannels(channels.data);
  const facts: ChecklistFacts = {
    hasPhoneChannel: active.some((c) => PHONE_PROVIDERS.has(c.provider)),
    hasEmailChannel: active.some((c) => c.provider === "email"),
    emailOnlyAlerts: flags.emailOnlyAlerts,
    visitedMyWork: flags.visitedMyWork,
    skillCount: profile.data.skills?.length ?? 0,
    hasLanguage: (profile.data.skills ?? []).some((s) => LANGUAGE_SET.has(s)),
    hasCity: !!profile.data.locationCity?.trim(),
    isWorkspaceMember: chat.data.isMember,
    hasSentMessage: chat.data.hasSent,
  };
  return {
    status: "ready",
    checklist: buildChecklist(facts),
    missingEnforced: missingEnforced(facts),
    facts,
    completedSeen: flags.completedSeen,
  };
}

export function useChecklistFlag(): (flag: ChecklistFlag) => void {
  const { userId } = useAuth();
  return useCallback(
    (flag) => {
      if (userId) setFlag(userId, flag);
    },
    [userId],
  );
}
